import React from "react";
import { graphql, Link } from "gatsby";
import marked from "marked";
import striptags from 'striptags';

import BaseLayout from "../layouts/base-layout";

import ActionBar from '../components/common/promos/action-bar';
import AffinitiesPromo from '../components/collections/affinities-promo';
import CategoryLink from "../components/categories/category-link";
import ContributorByline from "../components/contributors/contributor-byline";
import EssayGrid from '../components/essays/essay-grid';
import FormattedEssay from "../components/essays/formatted-essay";
import FurtherReadingList from "../components/bibliography/further-reading-list";
import GridHeader from '../components/common/grid-header';
import HeadWrapper from '../components/layout/head-wrapper';
import IndexFooter from '../components/essays/index-footer';
import PrintShopEmbed from '../components/common/promos/print-shop-embed';
import ResourcesGrid from "../components/resources/resources-grid";
import TagCloud from '../components/tags/tag-cloud';

import { FootnoteReturnSvg } from '../components/common/svg';

import { shuffle } from '../helpers/data-helpers';

import '../styles/essays/essay-view.scss';

export const Head = ({ data }) => (
	<HeadWrapper
		title={striptags(marked(data.essay.data.Title).replace('<p>','').replace('</p>',''))}
	>
		<body className="has-share-buttons" />

		<meta
			id="metaTitle"
			name="title"
			content={striptags(marked(data.essay.data.Title).replace('<p>','').replace('</p>',''))}
		/>
		<meta
			id="ogTitle"
			property="og:title"
			content={striptags(marked(data.essay.data.Title).replace('<p>','').replace('</p>',''))}
		/>
		<meta
			id="ogImage"
			property="og:image"
			content={data.site.siteMetadata.imageHost + data.essay.data.Featured_Image_Path}
		/>
		<meta
			id="metaDescription"
			name="description"
			content={data.essay.data.Intro}
		/>
		{data.essay.data.Intro ? (
			<meta
				id="ogDescription"
				property="og:description"
				content={striptags(marked(data.essay.data.Intro).replace('<p>','').replace('</p>',''))}
			/>
		) : null}
		<meta
			id="ogUrl"
			property="og:url"
			content={`https://publicdomainreview.org/essay/${data.essay.data.Slug}/`}
		/>
	</HeadWrapper>
);

class Essay extends React.Component {
  constructor(props) {
    super(props);

    this.footnoteReferences = this.grabFootnoteReferences(props.data.essay.data.Body);
    this.footnoteContent = this.buildFootnoteContent(props.data.essay.data.Footnotes);
    this.introContent = this.processedIntro(props.data.essay.data.Intro);

    this.compileRelatedEssays = this.compileRelatedEssays.bind(this);
    this.formattedTitle = this.formattedTitle.bind(this);


    if (props.data.essay.data.Public_Domain_Resources) {
      this.publishedResources =
        props.data.essay.data.Public_Domain_Resources.filter(resource => resource.data.Published);
    } else {
      this.publishedResources = [];
    }

    this.state = {
      showFootnotes: false,
      topPageRelatedEdges: [],
      bottomPageRelatedEdges: [],
    }
  }

  componentDidMount() {
    let footnoteLaunchers = document.querySelectorAll('sup.footnote');
    footnoteLaunchers = Array.prototype.slice.call(footnoteLaunchers);

    footnoteLaunchers.forEach(el => el.addEventListener('click', () => this.setState({ showFootnotes: true })));

    const { topPageRelatedEdges, bottomPageRelatedEdges } =
      this.compileRelatedEssays(this.props.data.essay, this.props.data.allEssays.edges);

    this.setState({
      topPageRelatedEdges: topPageRelatedEdges,
      bottomPageRelatedEdges: bottomPageRelatedEdges,
    });
  }

  compileRelatedEssays(essay, allEssayEdges) {
    let relatedEssays = allEssayEdges.filter(({ node }) => {
      let related = false;

      if (essay.data.Categories && node.data.Categories) {
        node.data.Categories.forEach(category => {
          if (essay.data.Categories.includes(category)) {
            related = true;
          }
        });
      }

      return related;
    });

    relatedEssays = shuffle(relatedEssays);

    // TODO: I am leaving the slice of 3 related essays to be reintroduced
    // in some sidebar presentation later.
    return {
      topPageRelatedEdges: relatedEssays.slice(0, 3),
      bottomPageRelatedEdges: relatedEssays.slice(3, 11),
    };
  }

  compiledTags(essay) {
    let filteredTags = [];

    if (essay.data.Tags) {
      filteredTags = essay.data.Tags.filter(({ data }) => data.Link_Count > 1 && !data.No_Footer_Display);
      filteredTags = filteredTags.map(tag => { return { node: tag }});
    }

    return filteredTags;
  }

  contributorsBlock(contributors) {
    return contributors.map((contributor, idx) => (
      <>
      {contributor.data.Description ? (
        <p
          key={`contributor-${idx}`}
          dangerouslySetInnerHTML={{__html: marked(contributor.data.Description).replace('<p>','').replace('</p>','')}}
        />
      ) : null}
      </>
    ));
  }

  formattedTitle(title, pretitle) {
    const titleParts = title.split(':');

    return (
      <h1>
        {pretitle && (
          <span
            className="pretitle"
            dangerouslySetInnerHTML={{ __html: marked(pretitle).replace('<p>', '').replace('</p>', '')}}
          />
        )}
        <span
          className="title"
          dangerouslySetInnerHTML={{ __html: marked(titleParts[0]).replace('<p>', '').replace('</p>', '')}}
        />
        {(titleParts.length > 1) && (
          <span
            className="subtitle"
            dangerouslySetInnerHTML={{ __html: marked(titleParts[1]).replace('<p>', '').replace('</p>', '')}}
          />
        )}
      </h1>
    );
  }

  processedIntro(introContent) {
    let formattedText = introContent || '';
    formattedText = marked(formattedText);
    formattedText = formattedText.replace('<p>', '');
    formattedText = formattedText.replace('</p>', '');

    return formattedText;
  }

  buildFootnoteContent(footnotesSrc) {
    const footnoteRegex = /^\[\^([\S]+?)\]: ([\S\s]+?)[\n]*$/;
    if (footnotesSrc) {
      const footnoteLines = footnotesSrc.split('\n').filter(line => {
        return line.match(footnoteRegex);
      });
      const footnotes = footnoteLines.map(line => {
        return {
          ref: line.match(footnoteRegex)[1],
          content: line.match(footnoteRegex)[2],
        };
      })

      if (footnotes.length > 0) {
        const footnoteElements = this.footnoteReferences.map((ref, idx) => {
          const filteredFootnotes = footnotes.filter(el => el['ref'] === ref);

          if (filteredFootnotes.length > 0) {
            const footnote = filteredFootnotes[0];
            const n = idx + 1;
            const formattedText = marked(footnote['content']).replace('<p>', '').replace('</p>', '');

            return (
              <li id={`fn${n}`}>
                <span dangerouslySetInnerHTML={{__html: formattedText}} />
                <a className="footnote-return" href={`#ref${n}`} title={`Jump back to footnote ${n} in the text.`}>{ FootnoteReturnSvg }</a>
              </li>
            );
          }

          return null;
        });

        return (<ol>{footnoteElements.map(el => el)}</ol>);
      }
    }

    return null;
  }

  headlineLabel(essay) {
    if (essay.data.Series === 'Conjectures') {
      return (
        <Link to="/series/conjectures" className="label">Conjectures</Link>
      );
    } else if (essay.data.Series === 'Curator’s Choice') {
      return (
        <Link to="/series/curators-choice" className="label">Curator’s Choice</Link>
      );
    }

    return (
      <Link to="/essays" className="label">Essays</Link>
    );
  }

  // TODO: This footnote code is now repeated in the FormattedEssay component
  grabFootnoteReferences(essayText) {
    const footnoteAnchorRegex = /\[\^([\S]+?)\]/g;
    let anchorMatches = [];
    let m;

    do {
      m = footnoteAnchorRegex.exec(essayText);
      if (m) {
        anchorMatches.push(m);
      }
    } while (m);

    return Array.from(anchorMatches).map(match => match[1]);
  }

  render() {
    const essay = this.props.data.essay;
    
    const date = new Date(essay.data.Published_Date);
    const utcDate = Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), date.getUTCHours(), date.getUTCMinutes(), date.getUTCSeconds());
    
    const day = new Intl.DateTimeFormat('en-US', { day: 'numeric' }).format(utcDate);
    const month = new Intl.DateTimeFormat('en-US', { month: 'long' }).format(utcDate);
    const year = new Intl.DateTimeFormat('en-US', { year: 'numeric' }).format(utcDate);
    
    const dateline = `${month} ${day}, ${year}`;
    
    const bibliographyItems = (essay.data.Bibliography) ?
      essay.data.Bibliography.sort((a, b) => {
        if (a.data.Sort_Name === b.data.Sort_Name) {
          return a.data.Title.localeCompare(b.data.Title);
        }

        if (a.data.Sort_Name) {
          return a.data.Sort_Name.localeCompare(b.data.Sort_Name);
        }

        return -1;
      })
    : null;
    
    let indexItems = [];
    this.props.data.indexCategories.edges.forEach(({ node }) => {
      const categorySlug = node.data.Slug;
      const categoryLabel = node.data.Label;
      
      node.data.Index_Listings.forEach(({ data }) => {
        const listingSlug = data.Slug;
        const listingLabel = data.Label;
        
        data.Index_Links.forEach(({ data }) => {
          const articleType = data.Composite_Slug.split('/')[1];
          const compositeSlug = data.Composite_Slug.split('/')[2];
          const slug = compositeSlug.split('#')[0];
          const paragraphRef = compositeSlug.split('#')[1];
          
          if (articleType === 'essay' && slug === essay.data.Slug) {
            indexItems.push({
              categoryLabel,
              categorySlug,
              listingLabel,
              listingSlug,
              paragraphRef
            });
          }
        });
      });
    });
    
    return (
      <BaseLayout path={this.props.location.pathname}>
        <div className="page-width">
          <div className="essay-view" role="main">
            <div className="essay-header">
              <div className="headline">
                {this.headlineLabel(essay)}
              </div>
              {this.formattedTitle(essay.data.Title, essay.data['Pre_Title'])}
              {(essay.data.Contributors) ? (
                <p className="byline">
                  <ContributorByline contributors={essay.data.Contributors} />
                </p>
              ) : null}
            </div>
            <div className="essay-intro">
              {(essay.data.Special_Intro) ? (
                <p className="intro special-left-align" dangerouslySetInnerHTML={{__html: marked(essay.data.Special_Intro).replace('<p>','').replace('</p>','')}} />
              ) : (
                <p className="intro" dangerouslySetInnerHTML={{__html: this.introContent}} />
              )}
              <p className="small-metadata-headline">Published</p>
              <p className="date">
                {dateline}
              </p>

            </div>
            <div className="essay-body">
              <div className="essay__content">
                <FormattedEssay
                  essayText={this.props.data.essay.data.Body}
                  indexItems={indexItems}
                />
              </div>
            </div>
            <div className="essay-footer">
              {(this.footnoteContent) ? (
                <div className="essay__footnotes essay__footer__section">
                  <p className="essay__footer__subhead">
                    Notes

                    <button
                      className="action-target"
                      onClick={() => this.setState({ showFootnotes: !this.state.showFootnotes })}
                    >
                      {this.state.showFootnotes ? 'Hide' : 'Show Notes'}
                    </button>
                  </p>

                  <div className={`essay__footnotes__content-wrapper ${this.state.showFootnotes ? 'expanded' : 'collapsed'}`}>
                    {this.footnoteContent}
                  </div>
                </div>
              ) : null}
              {(this.publishedResources.length > 0) ? (
                <div className="essay__resources essay__footer__section">
                  <p className="essay__footer__subhead">Public Domain Works</p>
                  <ResourcesGrid resources={this.publishedResources} essayContext={this.props.data.essay.data.Slug} />
                </div>
              ) : null}
              {(essay.data.Bibliography) ? (
                <div className="essay__further-reading essay__footer__section">
                  <p className="essay__footer__subhead">Further Reading</p>
                  <FurtherReadingList items={bibliographyItems} />
                </div>
              ) : null}
              {indexItems && (indexItems.length > 0) && (
                <div className="essay__index essay__footer__section">
                  <p className="essay__footer__subhead">Indexed under…</p>
                  <IndexFooter
                    indexItems={indexItems}
                  />
                </div>
              )}
              {(essay.data.Postscript) ? (
                <div
                  className="essay__postscript essay__content"
                  dangerouslySetInnerHTML={{ __html: marked(this.props.data.essay.data.Postscript) }}
                />
              ) : null}
            </div>
            <div className="essay-contributors">
              {this.contributorsBlock(essay.data.Contributors)}
            </div>
            <div className="essay-license essay__content">
              <div className="essay__text-block">
                <div className="essay__center-content">
                  {(this.props.data.essay.data.Publication_Rights === "Custom License") ? (
                    <div
                      className="note-block"
                      dangerouslySetInnerHTML={{ __html: marked(this.props.data.essay.data.License_Note) }}
                    />
                  ) : (
                    <div className="note-block">
                      <p>
                        The text of this essay is published under a <a href="https://creativecommons.org/licenses/by-sa/4.0/" target="_blank" rel="noopener noreferrer">CC BY-SA</a> license, see <a href="/legal#reusing-our-articles" target="_blank">here</a> for details.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="essay-outbound">
              {(essay.data.Categories) ? (
                <div>
                  <p className="small-metadata-headline inline">Subjects</p>
                  <ul className="categories">
                    {essay.data.Categories.map((category, idx) => (
                      <li key={`category-${idx}`}><CategoryLink category={category} /></li>
                    ))}
                  </ul>
                </div>
              ) : null}
              {(this.compiledTags(essay).length > 0) ? (
                <>
                  <p className="small-metadata-headline">Tags</p>
                  <div className="tags">
                    <TagCloud tagEdges={this.compiledTags(essay)} />
                  </div>
                </>
              ) : null}
            </div>
            <div className="essay-related">
            </div>
            <div className="essay-meta">
              {(essay.data.Note) ? (
                <div
                  className="essay__special-message"
                  dangerouslySetInnerHTML={{ __html: marked(essay.data.Note) }} />
              ) : null}
            </div>
          </div>
        </div>
        {(essay.data.Affinities_Promo || essay.data.Print_Shop) && (
          <div role="complementary" aria-label="Shop for Items Associated With this Essay" className="page-width">
            {(this.props.data.essay.data.Affinities_Promo) && (
              <div className="essay-promo">
                <AffinitiesPromo />
              </div>
            )}
            {(essay.data.Print_Shop) ? (
              <div className="essay-print-shop">
                <PrintShopEmbed dataYaml={ essay.data.Print_Shop } />
              </div>
            ) : null}
          </div>
        )}

        {(essay.data.Series === 'Conjectures') ? (
          <div role="complementary" aria-label="Links to Other Essays in this Series" className="page-width essay__conjectures-links">
            <GridHeader title="Conjectures" />
            <EssayGrid essayEdges={this.props.data.conjectures.edges} noMargins={ true } />
          </div>
        ): null}
        {(this.state.bottomPageRelatedEdges && this.state.bottomPageRelatedEdges.length > 0) ? (
          <div role="complementary" aria-label="Links to Related Essays" className="page-width essay__related-essays">
            <GridHeader title="Related Essays" />
            <EssayGrid essayEdges={this.state.bottomPageRelatedEdges} noMargins={ true } />
          </div>
        ) : null}

        <div className="section-spaced-row page-width">
          <ActionBar />
        </div>
      </BaseLayout>
    )
  }
}

export default Essay;

export const query = graphql`
query($slug:String) {
  site {
    siteMetadata {
      imageHost
    }
  }
  conjectures: allAirtable(filter:{table:{eq:"Essays"}, data:{Published:{eq: true}, Slug: {ne: $slug}, Series: {eq: "Conjectures"}}},
    sort: {data: {Published_Date: DESC}}) {
    edges {
      node {
        data {
          Title
          Intro
          Slug
          Featured_Image_Path
          Categories
          Published_Date
          Contributors {
            data {
              Name
              Slug
            }
          }
        }
      }
    }
  }
  allEssays: allAirtable(filter:{table:{eq:"Essays"}, data: {Published:{eq: true}, Slug: {ne: $slug}, Series: {eq: null}}}) {
    edges {
      node {
        data {
          Title
          Intro
          Slug
          Featured_Image_Path
          Categories
          Published_Date
          Contributors {
            data {
              Name
              Slug
            }
          }
        }
      }
    }
  }
  essay: airtable(table: {eq: "Essays"}, data: {Slug: {eq: $slug}}) {
    data {
      Title
      Pre_Title
      Slug
      Intro
      Special_Intro
      Body
      Footnotes
      Categories
      Print_Shop
      Affinities_Promo
      Postscript
      Publication_Rights
      License_Note
      Featured_Image_Path
      Published_Date
      Legacy_URL
      Series
      Tags {
        data {
          Label
          Slug
          Link_Count
          No_Footer_Display
        }
      }
      Note
      Contributors {
        data {
          Name
          Slug
          Sort_Name
          Description
        }
      }
      Bibliography {
        data {
          Title
          Author
          Editor
          Translator
          Sort_Name
          Publisher
          Journal
          Collection
          Volume
          Number
          Pages
          Year
          Description
          ASIN
          US_Bookshop
          UK_Bookshop
          Publisher_URL
          Cover_Image_Path
          URL
          DOI
          Type
        }
      }
      Public_Domain_Resources {
        data {
          Headline
          Creator
          Illustrator
          Canonical_Year
          Fuzzy_Year
          Special_Date
          Category
          Notes
          Links
          Essays {
            data {
              Slug
              Title
            }
          }
          Published
        }
      }
    }
  }
  indexCategories:allAirtable(
    filter:{table: {eq:"Index Categories"}},
    sort:{data: {Sorting_Label: ASC}}
  ) {
    edges {
      node {
        data {
          Slug
          Label
          Sorting_Label
          Index_Listings {
            data {
              Slug
              Label
              Index_Links {
                data {
                  Display_Label
                  Composite_Slug
                  Composite_Featured_Image
                }
              }
            }
          }
        }
      }
    }
  }
}
`;
